import { filter, map } from 'lodash-es';

/**
 * Return contract management list filtered.
 *
 * @param state
 * @param getters
 * @param rootState
 * @returns {Array}
 */
export const contractListFiltered = (state, getters, rootState) => {
    const contractList = state.overview.items || [];
    const filterValue = state.overview.filter;
    const showMine = state.overview.show === 'mine';
    const userTwentyId = rootState.auth.user.contractManagerTwentyId || null;
    const search = state.overview.search || '';

    return filter(contractList, contract => {
        const searchValidation = contract.supplierName.toLowerCase().indexOf(search.toLowerCase()) !== -1;
        const filterValidation = filterValue ? contract.status === filterValue : true;
        const ownContractValidation = showMine ? contract.contractManagerId === userTwentyId : true;
        return filterValidation && ownContractValidation && searchValidation;
    });
};

/**
 * Return contract management list filtered and paginated.
 *
 * @param state
 * @param getters
 * @param rootState
 * @returns {*[]}
 */
export const contractListFilteredPaginated = (state, getters) => {
    const contractList = getters.contractListFiltered;
    const page = state.overview.page;
    const limit = state.overview.limit;

    return contractList.slice(page * limit - limit, page * limit);
};

/**
 * Return the contract management list max page.
 *
 * @param state
 * @param getters
 * @returns {number}
 */
export const contractListMaxPages = (state, getters) => {
    const contractList = getters.contractListFiltered || [];
    const limit = state.overview.limit;
    const maxPages = Math.ceil(contractList.length / limit);

    return maxPages !== 0 ? maxPages : 1;
};

/**
 * Get the contract management progress percentage.
 *
 * @param state
 */
export const calculateContractManagementProgress = (state) => {
    const progress = state.contractManagement.progress || [];
    let count = 0;
    let done = 0;
    for(let part in progress) {
        for(let subPart in progress[part]) {
            if (subPart === 'notes') continue;
            count++;
            // If field is done then increment done factor
            if (progress[part][subPart]) {
                done++;
            }
        }
    }
    return count && done ? Math.ceil(done / count * 100) : 0;
};

/**
 * Get the contract progress percentage.
 *
 * @param state
 * @returns {number}
 */
export const calculateContractProgress = (state) => {
    let count = 0;
    const item = state.contract.progress.item;
    for (let attribute in item) {
        if (item[attribute]) count++;
    }
    return Math.ceil((count / 10)  * 100);
};

/**
 * Get the next part and subpart in the contract management form flow.
 *
 * @param state
 * @returns {*}
 */
export const contractManagementPageForward = (state) => {
    const currentPart = state.contractManagement.form.part;
    const currentSubPart = state.contractManagement.form.subPart;
    const progress = state.contractManagement.progress;
    let found = false;

    for(let part in progress) {
        for(let subPart in progress[part]) {
            if (found) {
                return {part: part, subPart: subPart}
            }
            if (part === currentPart && subPart === currentSubPart) {
                found = true;
            }
        }
    }
    return null;
};

/**
 * Get the last part and subpart in the contract management form flow.
 *
 * @param state
 * @returns {*}
 */
export const contractManagementPageBackward = (state) => {
    const currentPart = state.contractManagement.form.part;
    const currentSubPart = state.contractManagement.form.subPart;
    const progress = state.contractManagement.progress;
    let lastPart = null;
    let lastSubPart = null;

    for(let part in progress) {
        for(let subPart in progress[part]) {
            if (part === currentPart && subPart === currentSubPart) {
                return lastPart ? {part: lastPart, subPart: lastSubPart} : null
            }
            lastPart = part;
            lastSubPart = subPart;
        }
    }
    return null;
};

/**
 * Get the next part for the contract form flow.
 *
 * @param state
 * @returns {*}
 */
export const contractPageForward = (state) => {
    const currentPart = state.contract.part;
    const progress = state.contract.progress.item;
    let found = false;

    for(let part in progress) {
        if (found) {
            return {part: part}
        }
        if (part === currentPart) {
            found = true;
        }
    }
    return null;
};

/**
 * Get the previous part for the contract form flow.
 *
 * @param state
 * @returns {*}
 */
export const contractPageBackward = (state) => {
    const currentPart = state.contract.part;
    const progress = state.contract.progress.item;
    let lastPart = null;

    for(let part in progress) {
        if (part === currentPart) {
            return lastPart ? {part: lastPart} : null
        }
        lastPart = part;
    }
    return null;
};

/**
 * Get progress object which determines which main parts are done, and if all is done
 *
 * @param state
 */
export const contractManagementProgressObject = (state) => {
    let progressObj = {
        basic: true,
        extended: true,
        done: true,
        all: true
    };
    const progress = state.contractManagement.progress;
    for(let part in progress) {
        for(let subPart in progress[part]) {
            if (subPart === 'notes') continue;
            if (!progress[part][subPart]) {
                progressObj[part] = false;
                progressObj['all'] = false;
            }
        }
    }
    return progressObj;
};

/**
 * Get progress object for the contract.
 *
 * @param state
 */
export const contractProgressObject = (state) => {
    let progressObj = {
        all: true,
    };
    const progress = state.contract.progress.item;
    for(let part in progress) {
        if (!progress[part]) {
            return false;
        }
    }
    return progressObj;
};

/**
 * Check if the current user can accept contract part.
 *
 * Returns true if:
 * - The user is contract manager and the contract is not ready for supplier
 * - If the user is not a contract manager and the contract is ready for supplier
 *
 * @param rootState
 * @param state
 * @returns {boolean|*}
 */
export const userCanAccept = (state, getters, rootState) => {
    const user = rootState.auth.user || null;
    const contract = state.contract.item || null;
    if (!user || !contract) {
        return false;
    }
    return (user.contractManager && !contract.readyForSupplier) || (!user.contractManager && contract.readyForSupplier);
};

/**
 * Check if the current selected part email has been sent and if so then return the date.
 *
 * @param state
 */
export const emailSent = (state) => {
    const item = state.contractManagement.item;

    if(item) {
        return {readyForBackOffice: item['readyForBackOffice'], readyForReview: item['readyForReview']};
    }
};

/**
 * Get sites for supplier which is a merge of all registered sites for the supplier
 * and newly created sites.
 *
 * @param state
 * @returns {*[]}
 */
export const getContractManagementSites = (state) => {
    if (state.contractManagement.supplier.items && state.contractManagement.form.extended) {
        return map(state.contractManagement.form.extended.siteInfo, site => {
            return {name: site.name, id: site.id, area: site.area};
        });
    }
    return [];
};

/**
 * Get the contract collapse object which is used to collapse elements in the list.
 *
 * @param state
 */
export const getContractCollapseObject = (state) => {
    const part = state.contract.part;
    if (state.contract.item) {
        const element = state.contract.item[part];
        switch (part) {
        case 'prices':
            return pricesCollapseObject(element);
        case 'sites':
            return sitesCollapseObject(element);
        case 'terms':
            return null;
        default:
            return defaultCollapseObject(element);
        }
    }
};

/**
 * Generate simple collapse object based on sites > accommodations
 *
 * @param element
 */
const defaultCollapseObject = (element) => {
    return map(element.sites, (site, index) => {
        return {
            collapse: index !== 0,
            accommodations: fillArray(site.accommodations.length)
        }
    });
};

/**
 * Generate collapse object for the prices part.
 *
 * @param element
 */
const pricesCollapseObject = (element) => {
    return map(element.priceGroups, (price, index) => {
        return {
            collapse: index !== 0,
            sites: map(price.sites, (site, index) => {
                return {
                    collapse: index !== 0,
                    accommodations: fillArray(site.accommodations.length)
                }
            })
        }
    })
};

/**
 * Generate collapse object for the sites part.
 *
 * @param element
 * @returns {Array}
 */
const sitesCollapseObject = (element) => {
    return map(element.sites, (element, index) => {
        return {
            collapse: index !== 0
        }
    });
};

/**
 * Fill array with boolean values for the collapse object.
 * First element should not be collapsed.
 *
 * @param length
 * @returns {any[]}
 */
const fillArray = (length) => {
    let array = Array(length).fill(true);
    array[0] = false;
    return array;
};

/**
 * Check if the part of the contract is accepted.
 *
 * @param state
 */
export const contractPartAccepted = (state) => {
    const part = state.contract.part;
    const item = state.contract.form.item;
    if (part && item) {
        return item[part].accepted;
    }
};



// WEBPACK FOOTER //
// ./src/store/modules/contract/getters.js